import React from 'react';
import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4 } from '@/components/Element/Heading';
import MetaTags from '@/components/Head/MetaTags';
// import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import Image from 'next/legacy/image';
import SideCardContent from '@/components/SideCardContent/SideCardContent';
import SideCardCTA from '@/components/SideCardCTA/SideCardCTA2';
import Link from 'next/link';
import Image1 from 'images/solar-guide/image55.png';
import FullWidthGuideFormSection from '@/components/LeadForm/FullWidthGuideForm';
import ReadingTime from '@/components/ReadingTime/ReadingTime';
import { SolarGuideTable } from './why-go-solar';

const Page = () => {
  return (
    <article>
      <MetaTags
        title="Calculating Solar Production"
        description="2023 Solar Guide"
      />
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Home Solar Guide',
            href: '/home-solar-guide',
          },
          {
            name: '🧮 Calculating Solar Production',
            href: '/home-solar-guide/calculating-solar-production',
          },
        ]}
      />
      <CenteredContentSection>
        <div className="flex justify-between items-center">
          <Link href={'/home-solar-guide/what-affects-solar-project-viability'}>
            ← What Affects Solar Project Viability?
          </Link>{' '}
          <Link
            href={
              '/home-solar-guide/choosing-the-right-solar-system-for-your-home'
            }
          >
            Choosing the Right Solar System for Your Home →
          </Link>
        </div>
        <section id="section1">
          <H3>🧮 Calculating Solar Production</H3>
          <ReadingTime wordCount={670} />
          <p>
            Now that we know the factors that affect a project’s viability how
            do we accurately estimate how much solar power will be generated? If
            we want to calculate how much you can save we need to answer the
            following three questions:
          </p>
          <ol>
            <li>
              <strong>What is the cost of the system?</strong>
              <p>
                On average, residential solar panels cost between $2.50 and
                $3.50 per watt. Learn more about costs{' '}
                <Link href={'/solar-faq/how-much-do-solar-panels-cost'}>
                  here.
                </Link>
              </p>
            </li>
            <li>
              <strong>
                What is the value of energy produced by the system?
              </strong>
              <p>
                The value of energy produced by the system depends on local
                energy rates and consumption patterns, but it generally provides
                long-term cost savings on electricity bills and potential
                revenue through{' '}
                <Link href={'/solar-faq/what-is-net-metering'}>
                  net metering
                </Link>{' '}
                or feed-in tariffs.
              </p>
            </li>
            <li>
              <strong>
                How much solar energy (kWh) can be generated per panel (or kW)?
              </strong>
              <p>
                The amount of solar energy generated per panel or kilowatt (kW)
                depends on factors such as geographical location, panel
                efficiency, and weather conditions.
              </p>
            </li>
            <p>
              This next section will help you understand how we calculate solar
              production.
            </p>
          </ol>
          <H4>Determine the climate data</H4>
          <p>
            Above we’ve discussed the various items which can impact the local
            climate. But how does all this tie into solar?
          </p>
          <p>
            We take the historical climate patterns at a particular location and
            average them out to create a “synthetic” year. This represents the
            expected climate at that location over the long run. Various days of
            different weather conditions are included per month based on the
            historical data. This climate data is prepared and maintained by
            government entities and our data sources include:
          </p>
          <ul>
            <li>National Renewable Energy Laboratory - TMY3, PSM V3 (USA)</li>
            <li>Canadian Engineering Climate Services Unit – CWEC (Canada)</li>
            <li>
              ASHRAE - International Weather for Energy Calculations, v 2.0
              (Global)
            </li>
          </ul>
          <p>
            The actual weather experienced in a particular year can differ from
            the average by up to 10%, but is typically much less.
          </p>
          <H4>Model the Site</H4>
          <p>
            We use high quality overhead imagery to create 3D models of the
            house and nearby obstructions. We also use LIDAR data to aid and
            support in our design work.
          </p>
          <p>
            LIDAR, which stands for Light Detection and Ranging, is a remote
            sensing method that uses light in the form of a pulsed laser to
            measure ranges (variable distances) to the Earth. These light
            pulses—combined with other data recorded by the airborne system —
            generate precise, three-dimensional information about the shape of
            the Earth and its surface characteristics.
          </p>
          <p>
            <strong>
              This LIDAR data allows us to accurately determine several key
              characteristics:
            </strong>
          </p>
          <ul>
            <li>The height and tilt of the roof</li>
            <li>The height of nearby houses</li>
            <li>The height and shape of any nearby trees</li>
            <li>The height and shape of any rooftop obstructions</li>
          </ul>
          <p> Sample LIDAR Image:</p>
          <div className="mx-auto w-96">
            <Image src={Image1} alt="lidar scan of houses." />
          </div>
          <p>
            We then run a model to see how much sunlight each part of the house
            will get by simulating each hour over an entire year. This
            simulation factors in sun position, our climate data, roof
            orientation, roof tilt, and modeled obstructions. The result is an
            irradiance map outlining how efficient each roof section is.
          </p>
          <H4>Place panels</H4>
          <p>
            Now, we can lay out solar panels on the available roof space and
            simulate how much electricity they will produce. We factor in the
            same sun position, orientation, tilt, and obstruction data (shading)
            as before, but we also include the following additional factors:
          </p>
          <p>
            <strong>Light-Induced Degradation</strong> (typical 1.5%): Solar
            modules experience a loss in output power when they are initially
            exposed to sunlight. This is dependent on the particular module.
          </p>
          <p>
            <strong>Snow</strong> (typical varies by location): In some areas
            snow cover can affect energy production of solar installations. This
            information is determined using our climate data.
          </p>
          <p>
            <strong>Soiling</strong> (default 2%): Soiling accounts for dirt and
            dust accumulation on the solar panels. Depending on the location of
            the project this can vary.
          </p>
          <p>
            <strong>Connections</strong> (typical 0.5%): Represents resistive
            losses in connectors in the system
          </p>
          <p>
            <strong>Wiring</strong> (typical 2%): This accounts for resistive
            losses in the wiring of the system (both on the DC and AC side).
          </p>
          <p>
            <strong>Age</strong> (typical 0.25-0.5% per year): We factor in an
            expected degradation in performance over the{' '}
            <Link href="/solar-faq/how-long-do-solar-panels-last">
              life of the system
            </Link>{' '}
            based on the equipment chosen.
          </p>
          <p>
            <strong>System Availability</strong> (typical 1-3%): System
            availability accounts for downtime of the system due to maintenance
            and grid outages.
          </p>
          <p>
            Once complete we have a profile which will tell us how much each
            panel in the system is expected to produce each year.
          </p>
          <p>
            Fortunately, you don&apos;t have to do any of these calculations
            yourself as they&apos;re all handled by our System. The point is, we
            put a lot of effort into properly evaluating a solar site to make
            sure our systems produce what we say they will!
          </p>
        </section>
      </CenteredContentSection>
      <SideCardContent titles={['The Difficulty of Installation']} />
      <SideCardCTA />
      <FullWidthGuideFormSection cid={'a4oqmxxt5un31ha0'} />
      <SolarGuideTable />
    </article>
  );
};

export default Page;
